// ---------------------------------------------------------------------------
// Shared validation helpers for Patient/Therapist forms.
// All functions return either '' (valid) or an error message string.
// ---------------------------------------------------------------------------

const NAME_RE = /^[A-Za-z][A-Za-z\s'.-]*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateFirstName(v) {
  const s = (v || '').trim();
  if (!s) return 'First name is required.';
  if (s.length < 2 || !NAME_RE.test(s))
    return 'First name must be at least 2 characters and contain only letters.';
  return '';
}

export function validateLastName(v) {
  const s = (v || '').trim();
  if (!s) return ''; // optional
  if (s.length < 2 || !NAME_RE.test(s))
    return 'Last name must contain only letters (min 2 characters).';
  return '';
}

export function validateEmail(v) {
  const s = (v || '').trim();
  if (!s) return 'Email is required.';
  if (!EMAIL_RE.test(s)) return 'Please enter a valid email address.';
  return '';
}

export function validatePhoneE164(v) {
  const s = (v || '').trim();
  if (!s) return 'Phone number is required.';
  // E.164 form +<countrycode><digits>, total digits between 8 and 15
  if (!/^\+\d{8,15}$/.test(s))
    return 'Please enter a valid phone number.';
  return '';
}

export function validateOptionalPhoneE164(v) {
  const s = (v || '').trim();
  if (!s) return '';
  if (!/^\+\d{8,15}$/.test(s))
    return 'Please enter a valid phone number.';
  return '';
}

export function validateDOB(v, { minAge = null, maxAge = 120, label = 'Date of birth' } = {}) {
  if (!v) return `${label} is required.`;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return `Please enter a valid ${label.toLowerCase()}.`;
  const now = new Date();
  if (d > now) return `${label} cannot be in the future.`;
  const years = (now - d) / (1000 * 60 * 60 * 24 * 365.25);
  if (years > maxAge) return `${label} cannot be more than ${maxAge} years ago.`;
  if (minAge != null && years < minAge) {
    if (label.toLowerCase().includes('child'))
      return 'Child clients must be under 18. Please use Adult client type for clients 18 and above.';
    return `Client must be at least ${minAge} years old.`;
  }
  return '';
}

export function validateChildDOB(v) {
  if (!v) return 'Date of birth is required.';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return 'Please enter a valid date of birth.';
  const now = new Date();
  if (d > now) return 'Date of birth cannot be in the future.';
  const years = (now - d) / (1000 * 60 * 60 * 24 * 365.25);
  if (years >= 18)
    return 'Child clients must be under 18. Please use Adult client type for clients 18 and above.';
  if (years > 120) return 'Please enter a valid date of birth.';
  return '';
}

export function validateAdultDOB(v) {
  if (!v) return 'Date of birth is required.';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return 'Please enter a valid date of birth.';
  const now = new Date();
  if (d > now) return 'Date of birth cannot be in the future.';
  const years = (now - d) / (1000 * 60 * 60 * 24 * 365.25);
  if (years > 120) return 'Please enter a valid date of birth.';
  return '';
}

export function validateRelationshipDOB(v) {
  if (!v) return 'Date of birth is required.';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return 'Please enter a valid date of birth.';
  const now = new Date();
  if (d > now) return 'Date of birth cannot be in the future.';
  const years = (now - d) / (1000 * 60 * 60 * 24 * 365.25);
  if (years > 120) return 'Please enter a valid date of birth.';
  if (years < 18)
    return 'Relationship clients must be 18 or above. Please use Child client type for clients under 18.';
  return '';
}

export function validateGender(v) {
  return v ? '' : 'Please select a gender.';
}

export function validatePassword(v) {
  if (!v) return 'Password is required.';
  if (v.length < 8 || !/[A-Z]/.test(v) || !/[a-z]/.test(v) || !/\d/.test(v))
    return 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number.';
  return '';
}

export function passwordStrength(v) {
  if (!v) return { score: 0, label: '', color: '' };
  let score = 0;
  if (v.length >= 8) score += 1;
  if (/[A-Z]/.test(v)) score += 1;
  if (/[a-z]/.test(v)) score += 1;
  if (/\d/.test(v)) score += 1;
  if (/[^A-Za-z0-9]/.test(v)) score += 1;
  if (v.length >= 12) score += 1;
  // 'Strong' once the four mandatory rules pass (8+ chars + upper + lower + digit);
  // a special char or 12+ chars still bumps the score for the bar fill.
  if (v.length >= 8 && /[A-Z]/.test(v) && /[a-z]/.test(v) && /\d/.test(v))
    return { score: Math.max(score, 4), label: 'Strong', color: '#2E8B57' };
  if (score <= 2) return { score, label: 'Weak', color: '#D3544E' };
  return { score, label: 'Medium', color: '#F5B041' };
}

export function validatePrimaryTherapist(v) {
  return v ? '' : 'Please assign a primary therapist.';
}

export function validateCaregiverRelationship(v) {
  return v ? '' : "Please select the caregiver's relationship to the child.";
}

export function countErrors(errors) {
  return Object.values(errors || {}).filter((e) => !!e).length;
}
